import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Trash2, RefreshCw, Pencil, Check, X } from "lucide-react";
import { fetchGallerySupabase, deleteGalleryImageSupabase, updateGalleryImageSupabase, galleryImageUrlSupabase } from "../lib/supabase";
import GalleryUploadModal from "./GalleryUploadModal";

export default function GalleryManager() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null); // image id
  const [draft, setDraft] = useState({ category: "", caption: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try { setImages(await fetchGallerySupabase()); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => {
    const seen = [];
    images.forEach((im) => { if (!seen.includes(im.category)) seen.push(im.category); });
    return seen;
  }, [images]);

  const shown = filter === "all" ? images : images.filter((im) => im.category === filter);

  const remove = async (im) => {
    if (!window.confirm("Delete this photo? This cannot be undone.")) return;
    await deleteGalleryImageSupabase(im.id);
    await load();
  };

  const startEdit = (im) => { setEditing(im.id); setDraft({ category: im.category, caption: im.caption || "" }); };
  const saveEdit = async (id) => {
    await updateGalleryImageSupabase(id, { category: draft.category.trim() || "General", caption: draft.caption.trim() });
    setEditing(null);
    await load();
  };

  return (
    <div className="admin-panel">
      <div className="admin-panel-head">
        <h2>Manage Gallery <span style={{ color: "var(--muted)", fontWeight: 600, fontSize: "1rem" }}>· {images.length} photos</span></h2>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-ghost btn-sm" style={{ color: "var(--ink)", borderColor: "var(--line)", background: "#fff" }} onClick={load} data-testid="gallery-refresh"><RefreshCw size={15} /> Refresh</button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowUpload(true)} data-testid="gallery-upload-btn"><Plus size={16} /> Upload Photos</button>
        </div>
      </div>

      <div className="admin-filters">
        <button className={`chip-filter ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")}>All ({images.length})</button>
        {categories.map((c) => (
          <button key={c} className={`chip-filter ${filter === c ? "active" : ""}`} onClick={() => setFilter(c)}>
            {c} ({images.filter((im) => im.category === c).length})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="page-loader"><div className="spinner" /></div>
      ) : shown.length === 0 ? (
        <div className="empty-state">No photos in this category. Click “Upload Photos” to add some.</div>
      ) : (
        <div className="gm-grid" data-testid="gallery-manage-grid">
          {shown.map((im) => (
            <div key={im.id} className="gm-card" data-testid={`gm-${im.id}`}>
              <div className="gm-thumb">
                <img src={galleryImageUrlSupabase(im.image_url)} alt={im.category} loading="lazy" />
                <button className="gm-del" onClick={() => remove(im)} title="Delete" data-testid={`gm-delete-${im.id}`}><Trash2 /></button>
              </div>
              {editing === im.id ? (
                <div className="gm-edit">
                  <input value={draft.category} onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))} placeholder="Category" data-testid={`gm-cat-${im.id}`} />
                  <input value={draft.caption} onChange={(e) => setDraft((d) => ({ ...d, caption: e.target.value }))} placeholder="Caption" />
                  <div className="gm-edit-actions">
                    <button className="icon-btn" onClick={() => saveEdit(im.id)} data-testid={`gm-save-${im.id}`}><Check /></button>
                    <button className="icon-btn" onClick={() => setEditing(null)}><X /></button>
                  </div>
                </div>
              ) : (
                <div className="gm-meta">
                  <div>
                    <div className="gm-cat">{im.category}</div>
                    {im.caption && <div className="gm-cap">{im.caption}</div>}
                  </div>
                  <button className="icon-btn" onClick={() => startEdit(im)} title="Edit" data-testid={`gm-edit-${im.id}`}><Pencil /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showUpload && (
        <GalleryUploadModal
          existingCategories={categories}
          onClose={() => setShowUpload(false)}
          onDone={async () => { setShowUpload(false); await load(); }}
        />
      )}
    </div>
  );
}
